import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Promote-to-Leader modal.
 *
 * Flow:
 *   1. Confirm the person being promoted + their current closest upline.
 *   2. Multi-select the (optional) people to move into the new team.
 *   3. Confirmation summary with 3 actions: Confirm / Promote-now-assign-later / Cancel.
 *
 * Permissions enforced server-side — Admin anywhere, Platinum only in own branch.
 * This UI calls GET /users/{id}/reassignable-candidates for the candidate list,
 * and POST /users/{id}/promote-to-leader to commit.
 */
export default function PromoteLeaderDialog({ open, onClose, target, onDone }) {
  const [step, setStep] = useState("select");   // select | review
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !target) return;
    setStep("select"); setSelected(new Set()); setFilter("");
    setLoading(true);
    api.get(`/users/${target.id}/reassignable-candidates`)
      .then((r) => setCandidates(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load candidates"))
      .finally(() => setLoading(false));
  }, [open, target]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [candidates, filter]);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const chosen = useMemo(() => candidates.filter((c) => selected.has(c.id)), [candidates, selected]);

  const commit = async (moveIds) => {
    setBusy(true);
    try {
      await api.post(`/users/${target.id}/promote-to-leader`, {
        move_user_ids: moveIds,
      });
      toast.success(`${target.name} promoted to Leader${moveIds.length ? ` · ${moveIds.length} moved` : ""}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally { setBusy(false); }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="promote-dialog">
        <DialogHeader>
          <DialogTitle>Promote {target.name} to Leader</DialogTitle>
          <DialogDescription>
            Current closest upline: <b>{target.upline_name || "—"}</b> · Team: <b>{target.upline_team || "—"}</b>
          </DialogDescription>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              Do any existing people need to move into {target.name}&rsquo;s new team?
              <span className="text-slate-500"> (optional — you can also assign later)</span>
            </div>
            <Input
              placeholder="Search people…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="promote-search"
            />
            <div className="max-h-80 overflow-auto border rounded-md divide-y" data-testid="promote-candidates">
              {loading && <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-4 text-sm text-slate-500">No candidates available.</div>
              )}
              {filtered.map((c) => (
                <label key={c.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`promote-candidate-${c.id}`}>
                  <Checkbox
                    checked={selected.has(c.id)}
                    onCheckedChange={() => toggle(c.id)}
                    data-testid={`promote-candidate-check-${c.id}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{c.name} <span className="text-xs text-slate-400 ml-1">· {c.role}</span></div>
                    <div className="text-xs text-slate-500">
                      Currently under {c.current_upline?.name || "—"} · Team {c.upline_team || "—"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3 text-sm">
            <div className="font-serif text-lg">Confirm</div>
            <div className="rounded-md bg-slate-50 border p-3 space-y-1">
              <div><b>Promoting:</b> {target.name}</div>
              <div><b>Current closest upline:</b> {target.upline_name || "—"}</div>
              <div><b>New role:</b> Leader</div>
            </div>
            {chosen.length > 0 ? (
              <>
                <div className="font-medium">{chosen.length} people will move under {target.name}:</div>
                <ul className="border rounded-md divide-y" data-testid="promote-review-list">
                  {chosen.map((c) => (
                    <li key={c.id} className="p-2 text-sm">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-slate-500"> · was under {c.current_upline?.name || "—"} → now under {target.name}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="text-slate-500">Nobody will move at this time — you can assign the team later via Manage Team Members.</div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "select" && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="promote-cancel">Cancel</Button>
              <Button variant="secondary" onClick={() => commit([])} disabled={busy} data-testid="promote-later">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Promote now — assign team later"}
              </Button>
              <Button onClick={() => setStep("review")} data-testid="promote-review" disabled={selected.size === 0}>
                Review ({selected.size})
              </Button>
            </>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={() => setStep("select")} data-testid="promote-back" disabled={busy}>Back</Button>
              <Button onClick={() => commit([...selected])} disabled={busy} data-testid="promote-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Confirm promotion and assignments"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
